import { useState } from 'react';
import * as Sentry from '@sentry/react';
import { Button, TextButton, Typography, useOverlayContext } from 'seagull';
import ExternalLinkButton from "../components/ExternalLinkButton";
import { useStore, useStoreActions } from "../contexts";
import { helpArticles } from "../utils/helpArticles";
import { BankAccountDetailsModal } from "../BankAccountDetailsModal";
import { PlaidMode, usePlaidState } from "./usePlaidState";
import { BusinessChannelEventBinder, PusherEvent } from "../pusher";
import { selectors } from "../store/state-selectors";
import ContactSupportButton from "../components/ContactSupportButton";
import { isBankAccountConnected } from "../store/store.model";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export const useConnectBankAccount = () => {
  const [plaidCanceled, setPlaidCanceled] = useState(false);
  const [isConnectingInProgress, setIsConnectingInProgress] = useState(false);
  const [showingManualBankAccountModal, setShowingManualBankAccountModal] = useState(false);
  const overlayContext = useOverlayContext();
  const {
    api,
    logBI,
    fetchUserInfo
  } = useStoreActions();
  const plaidLink = usePlaidState({
    mode: {
      type: PlaidMode.Vendor
    },
    onSuccess(_account) {
      logBI(11210);
    },
    onError(_error) {
      logBI(11211);
      setPlaidCanceled(true);
      setIsConnectingInProgress(false);
    },
    onExit() {
      setPlaidCanceled(true);
      setIsConnectingInProgress(false);
    }
  });
  const onVendorBankAccountConnectedPushEvent = async _event => {
    await fetchUserInfo();
    setIsConnectingInProgress(false);
  };
  const ConnectBankAccountEventBinder = () => /*#__PURE__*/_jsx(BusinessChannelEventBinder, {
    source: "mgmt",
    eventName: PusherEvent.VendorBankAccountConnected,
    callback: onVendorBankAccountConnectedPushEvent
  });
  const onDetailsSubmit = async _ref => {
    let {
      routingNumber,
      accountNumber
    } = _ref;
    logBI(11212);
    setShowingManualBankAccountModal(false);
    overlayContext.startLoader();
    try {
      await api.vendor.authorizeVendorBankAccount({
        routingNumber,
        accountNumber
      });
    } catch (err) {
      Sentry.captureException(err);
      overlayContext.showErrorToast('Error submitting bank account details');
      logBI(11213);
    } finally {
      overlayContext.stopLoader();
    }
  };
  const onDetailsCancel = () => {
    setShowingManualBankAccountModal(false);
  };
  const Description = _ref2 => {
    let {
      testid,
      linkTestid,
      className
    } = _ref2;
    return /*#__PURE__*/_jsxs("div", {
      "data-testid": testid,
      className: className,
      children: [/*#__PURE__*/_jsx(Typography, {
        children: "Add your bank account details to receive payments. "
      }), /*#__PURE__*/_jsxs(Typography, {
        children: ["Note that ACH fees may apply. ", /*#__PURE__*/_jsx(ExternalLinkButton, {
          href: helpArticles.paymentsViaAnchor(),
          testid: linkTestid,
          openInNewTab: true,
          underline: "none",
          children: "Learn more"
        })]
      })]
    });
  };
  const ConnectButton = _ref3 => {
    let {
      testid,
      className,
      enterManuallyTestid
    } = _ref3;
    const userInfo = useStore(state => state.userInfo);
    const hasBankAccountConnected = selectors.user.hasBankAccountConnected({
      userInfo
    });
    if (hasBankAccountConnected) return /*#__PURE__*/_jsx(_Fragment, {});
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(ConnectBankAccountEventBinder, {}), /*#__PURE__*/_jsxs("div", {
        className: className,
        children: [/*#__PURE__*/_jsx(Button
        // mui button takes up fullwidth by default when wrapped by a flex-column container, this neutralizes that
        , {
          style: {
            width: 'fit-content'
          },
          size: "medium",
          "data-testid": testid,
          loading: isConnectingInProgress,
          onClick: () => {
            logBI(11204);
            setIsConnectingInProgress(true);
            plaidLink.open();
          },
          children: 'Connect bank'
        }), plaidCanceled && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(Typography, {
            "data-testid": "".concat(enterManuallyTestid, "-description"),
            style: {
              marginTop: '12px'
            },
            variant: "paragraph2",
            skin: "secondary",
            children: "Having trouble connecting your account?"
          }), /*#__PURE__*/_jsx(TextButton, {
            "data-testid": "".concat(enterManuallyTestid, "-btn"),
            onClick: () => {
              logBI(11205);
              setShowingManualBankAccountModal(true);
            },
            underline: "none",
            children: "Connect using routing and account number"
          })]
        })]
      }), showingManualBankAccountModal && /*#__PURE__*/_jsx(BankAccountDetailsModal, {
        open: true,
        onSubmit: onDetailsSubmit,
        onCancel: onDetailsCancel
      })]
    });
  };
  const BankAccountDetails = _ref4 => {
    let {
      payoutBankAccount
    } = _ref4;
    return isBankAccountConnected(payoutBankAccount) ? /*#__PURE__*/_jsxs("div", {
      style: {
        display: 'flex',
        flexDirection: 'column'
      },
      children: [/*#__PURE__*/_jsx(Typography, {
        "data-testid": "connected-bank-account-details",
        children: "The connected bank account is ****".concat(payoutBankAccount.connected.last4)
      }), /*#__PURE__*/_jsxs(Typography, {
        skin: "secondary",
        children: ["For bank account changes, please ", /*#__PURE__*/_jsx(ContactSupportButton, {
          underline: "none",
          testid: "contact-support"
        })]
      })]
    }) : /*#__PURE__*/_jsx(_Fragment, {});
  };
  return {
    Description,
    ConnectButton,
    BankAccountDetails
  };
};